import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { GLOBAL_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';
import invalidateData from '../helpers/invalidateData';
import { documentsAdminApi } from './documents.admin-api';
import { chaptersAdminApi } from './chapters.admin-api';
import { sectionsAdminApi } from './sections.admin-api';
import { logsAdminApi } from './logs.admin-api';
import { vendorsAdminApi } from './vendors.admin-api';

export type AdminUsers = {
    data: AdminUsersData;
};

export type AdminUsersData = AdminUser[];

export type AdminUser = {
    id: number;
    isSuperuser: boolean;
    isActive: boolean;
    fullName: string;
    email: string;
    mobilePhone: string;
    officePhone: string;
    country: string;
    title: string;
    userType: string;
    expirationDate: string | null;
    canAccessAppSettings: boolean;
    isSuperAdmin: boolean;
    permissions: AdminUserPermission[];
};

type AdminUserPermission = {
    id: number;
    name: string;
    codename: string;
};

type UserPermissionsResponse = {
    count: number;
    results: AdminUserPermission[];
};

type UserApiResponse = {
    id: number;
    is_superuser: boolean;
    is_active: boolean;
    full_name: string;
    email: string;
    mobile_phone: string;
    office_phone: string;
    country: string;
    title: string;
    user_type: string;
    expiration_date: string | null;
    can_access_app_settings: boolean;
    is_super_admin: boolean;
    token: string | undefined;
    permissions: {
        id: number;
        name: string;
        codename: string;
    }[];
};

type GetAllApiResponse = UserApiResponse[];

type UpdatePermissionsParams = {
    id: number;
    isSuperuser: boolean;
    isActive: boolean;
    permissions: GLOBAL_PERMISSIONS[],
    expirationDate: Date | null;
    canAccessAppSettings?: boolean;
    isSuperAdmin?: boolean;
};

type UpdateProfileParams = {
    id: number;
    fullName: string;
    email: string;
    mobilePhone: string;
    officePhone: string;
    country: string;
    title: string;
    userType: string;
};

type CreateUserData = {
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    user_type?: string;
    mobile_phone?: string;
    office_phone?: string;
    country?: string;
    title?: string;
};

type CreateUsersParams = {
    users: CreateUserData[];
    permission_ids?: number[];
};

type CreateUsersResponse = {
    message: string;
};

type DeactivateUserResponse = {
    message: string;
};

function transformGetAllResponse(raw: GetAllApiResponse): AdminUsers {
    return {
        data: raw.map((user) => ({
            id: user.id,
            isSuperuser: user.is_superuser,
            isActive: user.is_active,
            fullName: user.full_name,
            email: user.email,
            mobilePhone: user.mobile_phone,
            officePhone: user.office_phone,
            country: user.country,
            title: user.title,
            userType: user.user_type,
            expirationDate: user.expiration_date,
            canAccessAppSettings: user.can_access_app_settings,
            isSuperAdmin: user.is_super_admin,
            permissions: user.permissions.map((permission) => ({
                id: permission.id,
                name: permission.name,
                codename: permission.codename,
            })),
        })),
    };
}

export const usersAdminApi = createApi({
    reducerPath: 'users-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['User', 'UserPermission'],
    endpoints: (builder) => ({
        getAll: builder.query<AdminUsers, void>({
            query: () => ({
                url: '/users/',
                method: 'GET',
            }),
            transformResponse(raw: GetAllApiResponse) {
                return transformGetAllResponse(raw);
            },
            providesTags: ['User'],
        }),
        getUserPermissions: builder.query<UserPermissionsResponse, void>({
            query: () => ({
                url: '/user-permissions/',
                method: 'GET',
            }),
            providesTags: ['UserPermission'],
        }),
        create: builder.mutation<CreateUsersResponse, CreateUsersParams>({
            query: (params) => ({
                url: '/users/',
                method: 'POST',
                body: JSON.stringify(params),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['User'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    dispatch(documentsAdminApi.util.resetApiState());
                    dispatch(chaptersAdminApi.util.resetApiState());
                    dispatch(sectionsAdminApi.util.resetApiState());
                    dispatch(vendorsAdminApi.util.resetApiState());
                    dispatch(logsAdminApi.util.resetApiState());
                } catch (e) {
                    //
                }
            },
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/users/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['User'],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // REMOVE USER FROM LOCAL STATE
                    dispatch(
                        usersAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.filter((user) => user.id !== id),
                        })),
                    );

                    dispatch(documentsAdminApi.util.resetApiState());
                    dispatch(chaptersAdminApi.util.resetApiState());
                    dispatch(sectionsAdminApi.util.resetApiState());
                    dispatch(vendorsAdminApi.util.resetApiState());
                    dispatch(logsAdminApi.util.resetApiState());
                } catch (e) {
                    //
                }
            },
        }),
        deactivate: builder.mutation<DeactivateUserResponse, number>({
            query: (id) => ({
                url: `/users/${id}/deactivate/`,
                method: 'POST',
            }),
            invalidatesTags: ['User'],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // UPDATE USER STATUS IN LOCAL STATE
                    dispatch(
                        usersAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.map((user) => {
                                if (user.id === id) {
                                    return {
                                        ...user,
                                        isActive: false,
                                    };
                                }
                                return user;
                            }),
                        })),
                    );

                    dispatch(documentsAdminApi.util.resetApiState());
                    dispatch(chaptersAdminApi.util.resetApiState());
                    dispatch(sectionsAdminApi.util.resetApiState());
                    dispatch(vendorsAdminApi.util.resetApiState());
                    dispatch(logsAdminApi.util.resetApiState());
                } catch (e) {
                    //
                }
            },
        }),
        updatePermissions: builder.mutation<UserApiResponse, UpdatePermissionsParams>({
            query: (params) => {
                const {
                    isActive,
                    expirationDate,
                    canAccessAppSettings,
                    isSuperAdmin,
                } = params;

                const formData = JSON.stringify({
                    is_superuser: params.isSuperuser.toString(),
                    permissions_codenames: params.permissions.join(','),
                    is_active: isActive.toString(),
                    expiration_date: expirationDate?.toISOString() || '',
                    ...(canAccessAppSettings !== undefined && { can_access_app_settings: canAccessAppSettings.toString() }),
                    ...(isSuperAdmin !== undefined && { is_super_admin: isSuperAdmin.toString() }),
                });

                return {
                    url: `/users/${params.id}/permissions/`,
                    method: 'PATCH',
                    body: formData,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                };
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'User', id }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    dispatch(documentsAdminApi.util.resetApiState());
                    dispatch(chaptersAdminApi.util.resetApiState());
                    dispatch(sectionsAdminApi.util.resetApiState());
                    dispatch(vendorsAdminApi.util.resetApiState());
                    dispatch(logsAdminApi.util.resetApiState());

                    // UPDATE USER PERMISSIONS
                    dispatch(
                        usersAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.map((user) => {
                                if (user.id === queryData.id) {
                                    return {
                                        ...user,
                                        isActive: queryData.is_active,
                                        isSuperuser: queryData.is_superuser,
                                        expirationDate: queryData.expiration_date,
                                        canAccessAppSettings: queryData.can_access_app_settings,
                                        isSuperAdmin: queryData.is_super_admin,
                                        permissions: queryData.permissions.map((permission) => ({
                                            id: permission.id,
                                            name: permission.name,
                                            codename: permission.codename,
                                        })),
                                    };
                                }

                                return user;
                            }),
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        updateProfile: builder.mutation<UserApiResponse, UpdateProfileParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    full_name: params.fullName,
                    email: params.email,
                    mobile_phone: params.mobilePhone,
                    office_phone: params.officePhone,
                    country: params.country,
                    title: params.title,
                    user_type: params.userType,
                });

                return {
                    url: `/users/${params.id}/`,
                    method: 'PUT',
                    body: formData,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                };
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'User', id }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // UPDATE USER PROFILE IN LOCAL STATE
                    dispatch(
                        usersAdminApi.util.updateQueryData('getAll', undefined, (draft) => ({
                            data: draft.data.map((user) => {
                                if (user.id === queryData.id) {
                                    return {
                                        ...user,
                                        fullName: queryData.full_name,
                                        email: queryData.email,
                                        mobilePhone: queryData.mobile_phone,
                                        officePhone: queryData.office_phone,
                                        country: queryData.country,
                                        title: queryData.title,
                                        userType: queryData.user_type,
                                    };
                                }

                                return user;
                            }),
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
    }),
});

export const {
    useGetAllQuery: useGetAllUsers,
    
    useCreateMutation: useCreateUsers,
    useDeleteMutation: useDeleteUser,
    
    useUpdatePermissionsMutation: useUpdateUserPermissions,
    useUpdateProfileMutation: useUpdateUserProfile,
} = usersAdminApi;
